<!-- PublicDisenosView.vue — Página del cliente para aprobar o rechazar diseños -->
<!--
  Sin login: la credencial es el token del link que le llegó por WhatsApp.
  Se abre casi siempre desde el celular, así que está pensada primero para
  pantalla chica: un documento por tarjeta, visor incrustado y dos botones
  grandes. El comentario es obligatorio al rechazar porque es lo único que
  le dice a Ventas qué corregir.
-->
<template>
  <div class="public-view min-vh-100 py-4 py-md-5">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-12 col-lg-9 col-xl-8">
          <!-- ── Cargando ─────────────────────────────────── -->
          <div v-if="cargando" class="tarjeta text-center py-5">
            <div class="spinner-border text-verde"></div>
            <p class="mt-3 fw-bold text-cafe mb-0">Cargando tus diseños...</p>
          </div>

          <!-- ── Link inválido o ya cerrado ───────────────── -->
          <div v-else-if="errorCarga" class="tarjeta text-center p-4 p-md-5">
            <img
              src="@/assets/img/Divisan/logo_divisan.png"
              alt="Divisan"
              class="logo logo-solo mb-4"
            />
            <i :class="errorCarga.icono" :style="{ color: errorCarga.color }"></i>
            <h4 class="fw-black text-cafe mt-3">{{ errorCarga.titulo }}</h4>
            <p class="text-muted mb-0">{{ errorCarga.mensaje }}</p>
            <p class="contacto mt-4 mb-0">
              ¿Dudas? Contáctanos al <strong>844 893 5204</strong>
            </p>
          </div>

          <!-- ── Todo respondido: agradecimiento ──────────── -->
          <div v-else-if="terminado" class="tarjeta text-center p-4 p-md-5">
            <img
              src="@/assets/img/Divisan/logo_divisan.png"
              alt="Divisan"
              class="logo logo-solo mb-4"
            />
            <i class="bi bi-check-circle-fill icono-grande text-exito"></i>
            <h4 class="fw-black text-cafe mt-3">¡Gracias por tu respuesta!</h4>
            <p class="text-muted">
              Ya registramos tu revisión de la cotización
              <strong>{{ datos.cotizacion }}</strong
              >.
            </p>

            <div class="resumen-final">
              <div class="resumen-linea">
                <span><i class="bi bi-check-lg text-exito me-1"></i>Aprobados</span>
                <strong>{{ conteo.aprobados }}</strong>
              </div>
              <div v-if="conteo.rechazados" class="resumen-linea">
                <span><i class="bi bi-x-lg text-rechazo me-1"></i>Con ajustes</span>
                <strong>{{ conteo.rechazados }}</strong>
              </div>
            </div>

            <p class="text-muted small mt-3 mb-0">
              {{
                conteo.rechazados
                  ? "Nuestro equipo revisará tus comentarios y te contactará."
                  : "Continuamos con el proceso de tu pedido."
              }}
            </p>
            <p class="contacto mt-4 mb-0">
              ¿Dudas? Contáctanos al <strong>844 893 5204</strong>
            </p>
          </div>

          <!-- ── Revisión ─────────────────────────────────── -->
          <template v-else>
            <div class="tarjeta encabezado mb-3">
              <div class="d-flex align-items-center gap-3 flex-wrap">
                <img
                  src="@/assets/img/Divisan/logo_divisan.png"
                  alt="Divisan"
                  class="logo"
                />
                <div class="flex-grow-1">
                  <span class="etiqueta">Revisión de diseños</span>
                  <h5 class="fw-black text-cafe mb-0">
                    Cotización {{ datos.cotizacion }}
                  </h5>
                  <small class="text-muted">{{ datos.cliente }}</small>
                </div>
              </div>

              <div class="progreso mt-3">
                <div class="progreso-barra">
                  <div
                    class="progreso-relleno"
                    :style="{ width: `${porcentaje}%` }"
                  ></div>
                </div>
                <small class="progreso-texto">
                  {{ respondidos }} de {{ datos.pdfs.length }} revisados
                </small>
              </div>

              <p class="instruccion mt-3 mb-0">
                <i class="bi bi-info-circle me-1"></i>
                Revisa cada diseño y apruébalo, o recházalo indicando qué ajustar.
              </p>
            </div>

            <!-- Documentos -->
            <div v-for="pdf in datos.pdfs" :key="pdf.id" class="tarjeta documento mb-3">
              <div class="doc-encabezado">
                <div class="doc-titulo">
                  <i class="bi bi-file-earmark-pdf-fill text-rechazo"></i>
                  <span>{{ pdf.nombre_original }}</span>
                </div>

                <div class="doc-acciones-header">
                  <span v-if="pdf.estatus !== 'PENDIENTE'" :class="badge(pdf.estatus)">
                    <i
                      class="bi me-1"
                      :class="pdf.estatus === 'APROBADO' ? 'bi-check-lg' : 'bi-x-lg'"
                    ></i>
                    {{ pdf.estatus === "APROBADO" ? "Aprobado" : "Con ajustes" }}
                  </span>

                  <button
                    class="btn-ver"
                    :class="{ abierto: estaAbierto(pdf.id) }"
                    :title="estaAbierto(pdf.id) ? 'Ocultar diseño' : 'Ver diseño'"
                    @click="alternarVisor(pdf.id)"
                  >
                    <i
                      class="bi"
                      :class="estaAbierto(pdf.id) ? 'bi-eye-slash' : 'bi-eye'"
                    ></i>
                    {{ estaAbierto(pdf.id) ? "Ocultar" : "Ver" }}
                  </button>
                </div>
              </div>

              <!-- Visor: cerrado por default para no cargar todos los PDFs de
                   golpe; en celular eso se come los datos y tarda una eternidad. -->
              <template v-if="estaAbierto(pdf.id)">
                <div class="visor">
                  <embed :src="pdf.archivo" type="application/pdf" class="pdf-embed" />
                </div>
                <a
                  :href="pdf.archivo"
                  target="_blank"
                  rel="noopener"
                  class="abrir-aparte"
                >
                  <i class="bi bi-box-arrow-up-right me-1"></i>
                  ¿No se ve? Ábrelo en una pestaña nueva
                </a>
              </template>

              <p v-else class="visor-cerrado">
                <i class="bi bi-eye me-1"></i>
                Toca <strong>Ver</strong> para revisar este diseño.
              </p>

              <!-- Acciones (solo si sigue pendiente) -->
              <template v-if="pdf.estatus === 'PENDIENTE'">
                <div v-if="rechazando !== pdf.id" class="acciones">
                  <button
                    class="btn-accion btn-rechazar"
                    :disabled="enviando === pdf.id"
                    @click="iniciarRechazo(pdf)"
                  >
                    <i class="bi bi-x-lg me-1"></i>Rechazar
                  </button>
                  <button
                    class="btn-accion btn-aprobar"
                    :disabled="enviando === pdf.id"
                    @click="aprobar(pdf)"
                  >
                    <span
                      v-if="enviando === pdf.id"
                      class="spinner-border spinner-border-sm me-1"
                    ></span>
                    <i v-else class="bi bi-check-lg me-1"></i>
                    Aprobar
                  </button>
                </div>

                <!-- Formulario de rechazo -->
                <div v-else class="form-rechazo">
                  <label class="campo-label">
                    ¿Qué hay que ajustar? <span class="text-rechazo">*</span>
                  </label>
                  <textarea
                    v-model="comentario"
                    class="campo-textarea"
                    rows="3"
                    placeholder="Ej. el logo debe ir en azul, no en verde"
                    :disabled="enviando === pdf.id"
                    ref="textareaRechazo"
                  ></textarea>
                  <div class="acciones mt-2">
                    <button
                      class="btn-accion btn-cancelar"
                      :disabled="enviando === pdf.id"
                      @click="cancelarRechazo"
                    >
                      Cancelar
                    </button>
                    <button
                      class="btn-accion btn-rechazar"
                      :disabled="!comentario.trim() || enviando === pdf.id"
                      @click="rechazar(pdf)"
                    >
                      <span
                        v-if="enviando === pdf.id"
                        class="spinner-border spinner-border-sm me-1"
                      ></span>
                      Enviar respuesta
                    </button>
                  </div>
                </div>
              </template>

              <!-- Comentario ya enviado -->
              <div v-else-if="pdf.comentario_rechazo" class="comentario-enviado">
                <i class="bi bi-chat-left-quote-fill me-2"></i>
                <span>{{ pdf.comentario_rechazo }}</span>
              </div>
            </div>

            <div v-if="errorAccion" class="tarjeta error-accion mb-3">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ errorAccion }}
            </div>

            <p class="pie">
              Divisan · ¿Dudas? Contáctanos al <strong>844 893 5204</strong>
            </p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { useRoute } from "vue-router";
import { getDisenoPublico, responderDisenoPublico } from "@/utils/api_backend";

const route = useRoute();
const token = route.params.token;

const datos = ref(null);
const cargando = ref(true);
const errorCarga = ref(null);
const errorAccion = ref("");

const enviando = ref(null); // id del PDF en proceso
const rechazando = ref(null); // id del PDF con el formulario abierto
const comentario = ref("");
const textareaRechazo = ref(null);

// Visores abiertos. Va aparte de `datos` a propósito: cada respuesta reemplaza
// ese objeto completo con el que devuelve el backend, y una bandera guardada
// dentro de cada PDF se perdería, cerrando el visor que el cliente dejó abierto.
const visoresAbiertos = ref(new Set());

function estaAbierto(id) {
  return visoresAbiertos.value.has(id);
}

function alternarVisor(id) {
  const siguiente = new Set(visoresAbiertos.value);
  if (siguiente.has(id)) {
    siguiente.delete(id);
  } else {
    siguiente.add(id);
  }
  visoresAbiertos.value = siguiente;
}

// ── Derivados ──────────────────────────────────────────────
const respondidos = computed(
  () => datos.value?.pdfs.filter((p) => p.estatus !== "PENDIENTE").length || 0
);

const porcentaje = computed(() => {
  const total = datos.value?.pdfs.length || 0;
  return total ? Math.round((respondidos.value / total) * 100) : 0;
});

// Se muestra el agradecimiento cuando ya no queda nada por responder.
const terminado = computed(
  () =>
    !!datos.value &&
    datos.value.pdfs.length > 0 &&
    respondidos.value === datos.value.pdfs.length
);

const conteo = computed(() => ({
  aprobados: datos.value?.pdfs.filter((p) => p.estatus === "APROBADO").length || 0,
  rechazados: datos.value?.pdfs.filter((p) => p.estatus === "RECHAZADO").length || 0,
}));

// ── Carga ──────────────────────────────────────────────────
async function cargar() {
  cargando.value = true;
  errorCarga.value = null;
  try {
    datos.value = await getDisenoPublico(token);
  } catch (err) {
    errorCarga.value = interpretarError(err);
  } finally {
    cargando.value = false;
  }
}

// El 410 no es un error del cliente: el link cumplió su propósito. Se explica
// distinto del 404 para que no parezca que algo se rompió.
function interpretarError(err) {
  const estado = err.response?.status;

  if (estado === 410) {
    return {
      icono: "bi bi-check-circle-fill icono-grande",
      color: "#2e7d32",
      titulo: "Este link ya no está disponible",
      mensaje: "Los diseños de esta cotización ya fueron revisados. Gracias.",
    };
  }
  if (estado === 404) {
    return {
      icono: "bi bi-exclamation-triangle-fill icono-grande",
      color: "#e6a700",
      titulo: "Link no válido",
      mensaje:
        "Revisa que hayas abierto el enlace completo tal como te llegó por WhatsApp.",
    };
  }
  return {
    icono: "bi bi-wifi-off icono-grande",
    color: "#c62828",
    titulo: "No pudimos cargar tus diseños",
    mensaje: "Revisa tu conexión e intenta de nuevo en un momento.",
  };
}

// ── Acciones ───────────────────────────────────────────────
async function aprobar(pdf) {
  await responder(pdf, { accion: "aprobar" });
}

function iniciarRechazo(pdf) {
  rechazando.value = pdf.id;
  comentario.value = "";
  errorAccion.value = "";
  nextTick(() => {
    const campo = Array.isArray(textareaRechazo.value)
      ? textareaRechazo.value[0]
      : textareaRechazo.value;
    campo?.focus();
  });
}

function cancelarRechazo() {
  rechazando.value = null;
  comentario.value = "";
}

async function rechazar(pdf) {
  await responder(pdf, {
    accion: "rechazar",
    comentario: comentario.value.trim(),
  });
}

async function responder(pdf, payload) {
  enviando.value = pdf.id;
  errorAccion.value = "";
  try {
    // El backend devuelve el registro completo ya recalculado, así que se
    // reemplaza el estado en vez de parchar el PDF a mano.
    datos.value = await responderDisenoPublico(token, pdf.id, payload);
    rechazando.value = null;
    comentario.value = "";
  } catch (err) {
    const estado = err.response?.status;
    if (estado === 410) {
      // Alguien más ya cerró el registro (p. ej. otro rechazo).
      errorCarga.value = interpretarError(err);
    } else {
      errorAccion.value =
        err.response?.data?.detail ||
        err.response?.data?.comentario?.[0] ||
        "No se pudo enviar tu respuesta. Intenta de nuevo.";
    }
  } finally {
    enviando.value = null;
  }
}

function badge(estatus) {
  return `badge-estatus ${estatus === "APROBADO" ? "badge-aprobado" : "badge-rechazado"}`;
}

onMounted(cargar);
</script>

<style scoped>
.public-view {
  font-family: "Inter", sans-serif;
  background: linear-gradient(rgba(20, 30, 40, 0.55), rgba(20, 30, 40, 0.45)),
    url("@/assets/img/Divisan/img_2.jpg");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
}

.tarjeta {
  background: var(--d-cream);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  padding: 1.1rem 1.2rem;
}
.fw-black {
  font-weight: 900;
}
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-exito {
  color: #2e7d32;
}
.text-rechazo {
  color: #c62828;
}
.logo {
  height: 42px;
  width: auto;
}
/*
 * En las tarjetas centradas el logo va solo en su renglón. Sin esto queda en
 * línea junto al ícono de estado (ambos son elementos inline) y se empalman.
 * No se aplica a .logo a secas porque en el encabezado de revisión es un ítem
 * flex, y ahí un margen automático lo empujaría fuera de lugar.
 */
.logo-solo {
  display: block;
  margin-left: auto;
  margin-right: auto;
  height: 52px;
}
.icono-grande {
  display: block;
  font-size: 3.2rem;
  line-height: 1;
}

/* ── Encabezado ─────────────────────────────────────────── */
.etiqueta {
  display: block;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--d-green-dark);
}
.instruccion {
  font-size: 0.82rem;
  color: var(--d-brown);
  opacity: 0.75;
}

.progreso-barra {
  height: 7px;
  background: rgba(64, 34, 8, 0.12);
  border-radius: 20px;
  overflow: hidden;
}
.progreso-relleno {
  height: 100%;
  background: var(--d-green);
  border-radius: 20px;
  transition: width 0.35s ease;
}
.progreso-texto {
  display: block;
  margin-top: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--d-green-dark);
}

/* ── Documento ──────────────────────────────────────────── */
.doc-encabezado {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.doc-titulo {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--d-brown);
  word-break: break-all;
}
.doc-acciones-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.btn-ver {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 5px 13px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--d-green-dark);
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.btn-ver:hover,
.btn-ver.abierto {
  background: var(--d-green);
  color: var(--d-cream);
}

.visor-cerrado {
  margin: 0;
  padding: 14px 12px;
  background: rgba(96, 108, 56, 0.06);
  border: 1px dashed rgba(96, 108, 56, 0.35);
  border-radius: 10px;
  font-size: 0.82rem;
  color: var(--d-brown);
  opacity: 0.8;
  text-align: center;
}

.visor {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(64, 34, 8, 0.15);
  background: white;
}
.pdf-embed {
  width: 100%;
  height: 420px;
  display: block;
}
@media (min-width: 768px) {
  .pdf-embed {
    height: 560px;
  }
}
.abrir-aparte {
  display: inline-block;
  margin-top: 6px;
  font-size: 0.76rem;
  color: var(--d-green-dark);
  text-decoration: none;
  font-weight: 600;
}
.abrir-aparte:hover {
  text-decoration: underline;
}

.badge-estatus {
  font-size: 0.74rem;
  font-weight: 800;
  border-radius: 20px;
  padding: 3px 12px;
  white-space: nowrap;
}
.badge-aprobado {
  background: #e8f5e9;
  color: #1b5e20;
}
.badge-rechazado {
  background: #fdeaea;
  color: #c62828;
}

/* ── Acciones ───────────────────────────────────────────── */
.acciones {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}
.btn-accion {
  flex: 1;
  border: none;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 0.92rem;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.btn-accion:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.btn-aprobar {
  background: #2e7d32;
  color: white;
}
.btn-aprobar:hover:not(:disabled) {
  background: #1b5e20;
}
.btn-rechazar {
  background: white;
  color: #c62828;
  border: 1.5px solid #c62828;
}
.btn-rechazar:hover:not(:disabled) {
  background: #c62828;
  color: white;
}
.btn-cancelar {
  background: none;
  border: 1.5px solid rgba(96, 108, 56, 0.45);
  color: var(--d-green-dark);
}

/* ── Formulario de rechazo ──────────────────────────────── */
.form-rechazo {
  margin-top: 12px;
  padding: 12px;
  background: rgba(198, 40, 40, 0.05);
  border: 1px solid rgba(198, 40, 40, 0.2);
  border-radius: 10px;
}
.campo-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 800;
  color: var(--d-brown);
  margin-bottom: 5px;
}
.campo-textarea {
  width: 100%;
  border: 1px solid rgba(96, 108, 56, 0.4);
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 0.9rem;
  color: var(--d-brown);
  resize: vertical;
  outline: none;
  box-sizing: border-box;
}
.campo-textarea:focus {
  border-color: #c62828;
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.1);
}

.comentario-enviado {
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
  padding: 9px 12px;
  background: rgba(198, 40, 40, 0.07);
  border-left: 3px solid #c62828;
  border-radius: 0 8px 8px 0;
  font-size: 0.84rem;
  color: #b02020;
}

.error-accion {
  display: flex;
  align-items: center;
  color: #c62828;
  font-size: 0.86rem;
  font-weight: 600;
}

/* ── Cierre ─────────────────────────────────────────────── */
.resumen-final {
  max-width: 260px;
  margin: 1rem auto 0;
}
.resumen-linea {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 12px;
  background: rgba(96, 108, 56, 0.08);
  border-radius: 8px;
  font-size: 0.86rem;
  color: var(--d-brown);
  margin-bottom: 6px;
}
.contacto {
  font-size: 0.8rem;
  color: var(--d-brown);
  opacity: 0.7;
}
.pie {
  text-align: center;
  font-size: 0.78rem;
  color: var(--d-cream);
  opacity: 0.85;
  margin-top: 1rem;
}
</style>
